// Export buttons for one lead (04 §2) and for the current list.
//
// What is offered here is what actually works without asking the owner to wait for a megabyte of
// JavaScript: Markdown for the dossier, CSV for a list. PDF and DOCX are on the roadmap and are
// shown as such rather than as buttons that produce something disappointing — a PDF without proper
// Cyrillic embedding is worse than no PDF, because it looks fine until someone opens it.
//
// Every list cell goes through `safeCell` first. See safe-cell.ts for why that is not optional.

import { useDossier } from '../useDossier.js';
import type { LeadListRow } from '../useLeads.js';
import { asciiFilename, csvRow, download } from './safe-cell.js';
import { dossierToMarkdown } from './dossier-md.js';

export function DossierExport({ leadId, name }: { leadId: number; name: string }) {
  const { data: content } = useDossier(leadId);

  if (!content) return null;

  return (
    <div className="exports">
      <button
        type="button"
        onClick={() =>
          download(asciiFilename(name, 'md'), 'text/markdown', dossierToMarkdown(name, content))
        }
      >
        Изтегли досието (MD)
      </button>
      <span className="exports__soon">PDF и DOCX — предстоят</span>
    </div>
  );
}

const LIST_HEADERS = [
  'Име',
  'Град',
  'Район',
  'Клас',
  'Точки',
  'Приоритет',
  'Телефон',
  'Телефон потвърден',
  'Сайт',
  'Обекти на същия сайт',
] as const;

export function ListExport({ leads, label }: { leads: readonly LeadListRow[]; label: string }) {
  const toCsv = (): string => {
    const rows = leads.map((lead) =>
      csvRow([
        lead.business_name,
        lead.city_key,
        lead.area,
        lead.tier,
        lead.lead_score,
        lead.priority,
        lead.phone,
        lead.phone !== null ? 'да' : 'не',
        lead.website_url,
        lead.branchCount === undefined ? '' : lead.branchCount + 1,
      ]),
    );
    // The BOM is what makes Excel open a UTF-8 CSV as Cyrillic rather than as mojibake. Written
    // as an escape, not as the character itself — an invisible byte in source is a trap.
    return `\uFEFF${csvRow(LIST_HEADERS)}\n${rows.join('\n')}\n`;
  };

  return (
    <button
      type="button"
      className="exports__list"
      disabled={leads.length === 0}
      onClick={() => download(asciiFilename(label, 'csv'), 'text/csv', toCsv())}
    >
      Изтегли списъка ({leads.length})
    </button>
  );
}
